import axios from "axios";
import React, { useEffect, useReducer, useState } from "react";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import { getError } from "../utils";
import Card from "react-bootstrap/Card";
import ListGroup from "react-bootstrap/ListGroup";
import Badge from 'react-bootstrap/Badge';

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return {
        ...state,
        restaurants: action.payload.restaurants,
        menuItems: action.payload.menuItems,
        loading: false,
      };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function SearchScreen() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const query = sp.get("query") || "all";

  const [{ loading, error, restaurants, menuItems }, dispatch] = useReducer(reducer, {
    loading: true,
    error: "",
    restaurants: [],
    menuItems: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: "FETCH_REQUEST" });

        const searchQuery = query !== 'all' ? query : '';

        // Parallel fetch for restaurants and menu items
        const [restaurantsRes, menuItemsRes] = await Promise.all([
          axios.get(`/api/restaurants?query=${searchQuery}`),
          axios.get(`/api/menu?query=${searchQuery}`)
        ]);

        dispatch({
          type: "FETCH_SUCCESS",
          payload: {
            restaurants: restaurantsRes.data,
            menuItems: menuItemsRes.data
          }
        });
      } catch (err) {
        dispatch({
          type: "FETCH_FAIL",
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [query]);

  return (
    <div>
      <Helmet>
        <title>Search Results</title>
      </Helmet>

      <div className="mb-4">
        <h2>Results for "{query !== 'all' ? query : 'All'}"</h2>
      </div>

      {loading ? (
        <LoadingBox></LoadingBox>
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <>
          {/* Restaurants Section */}
          {restaurants.length > 0 && (
            <div className="mb-5">
              <h3 className="mb-3 border-bottom pb-2">Restaurants</h3>
              <Row>
                {restaurants.map((restaurant) => (
                  <Col key={restaurant._id} sm={6} md={4} lg={3} className="mb-4">
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        <Card.Title>{restaurant.name}</Card.Title>
                        <Card.Text className="text-muted small">
                          <i className="fas fa-map-marker-alt me-1"></i> {restaurant.location || 'Location not set'}
                        </Card.Text>
                        <div className="d-grid">
                          <Button
                            variant="outline-primary"
                            onClick={() => navigate(`/menu?business=${restaurant._id}`)}
                          >
                            View Menu
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Menu Items Section */}
          {menuItems.length > 0 && (
            <div>
              <h3 className="mb-3 border-bottom pb-2">Menu Items</h3>
              <Row>
                {menuItems.map((item) => (
                  <Col key={item._id} sm={6} md={4} lg={3} className="mb-4">
                    <Card className="h-100 shadow-sm">
                      <div className="position-relative">
                        <img src={item.image} className="card-img-top" alt={item.name} style={{ height: '200px', objectFit: 'cover' }} />
                        {item.restaurantId && (
                          <Badge bg="dark" className="position-absolute top-0 start-0 m-2">
                            {item.restaurantId.name}
                          </Badge>
                        )}
                      </div>
                      <Card.Body className="d-flex flex-column">
                        <Card.Title>{item.name}</Card.Title>
                        <Card.Text className="text-truncate-2 small text-muted mb-auto">
                          {item.description}
                        </Card.Text>
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <span className="fw-bold">{item.price} RWF</span>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/menu?business=${item.restaurantId?._id || item.restaurantId}`)}
                          >
                            Go to Restaurant
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {restaurants.length === 0 && menuItems.length === 0 && (
            <MessageBox>No results found.</MessageBox>
          )}
        </>
      )}
    </div>
  );
}
